/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Bug, TriangleAlert, type LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

interface DebugSectionConfig {
    description: string;
    icon: LucideIcon;
    label: string;
    route: string;
}

const DEBUG_SECTIONS: DebugSectionConfig[] = [
    {
        description: 'Inspect API requests made during the current browser session.',
        icon: Bug,
        label: 'Debug',
        route: APP_ROUTES.debug,
    },
    {
        description: 'Review application errors saved locally in this browser.',
        icon: TriangleAlert,
        label: 'Error Log',
        route: APP_ROUTES.errorlog,
    },
];

function DebugOverviewTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6" aria-label={t('Debug')}>
            <section className="flex flex-col gap-2">
                <h2>{t('Debug')}</h2>
                <p>{t('Select a debug tool to inspect the current application state:')}</p>
            </section>
            <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {DEBUG_SECTIONS.map((section) => {
                    const Icon = section.icon;

                    return (
                        <NavLink
                            key={section.route}
                            to={section.route}
                            className="dashboard-panel interactive-surface !min-h-44 no-underline transition-[background,transform] duration-150"
                        >
                            <div className="flex h-full w-full flex-col items-start justify-center gap-3 px-6 text-left">
                                <div className="flex w-full items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                        <Icon className="mt-0.5 h-5 w-5 shrink-0 text-base-content/75" aria-hidden="true" />
                                        <span className="text-xl font-semibold">{t(section.label)}</span>
                                    </div>
                                    <span className="badge badge-warning shrink-0">{t('Debug')}</span>
                                </div>
                                <span className="text-sm text-base-content/70">
                                    {t(section.description)}
                                </span>
                            </div>
                        </NavLink>
                    );
                })}
            </section>
        </div>
    );
}

export default DebugOverviewTab;
